import submit from "submit-form";
import { calculateWeeksWithDays } from "./date-helpers";
import { simpleHash } from "./string-helpers";

const DEFAULT_MEALS = [
  { type: "Breakfast", order: 1, foods: [] },
  { type: "Lunch", order: 2, foods: [] },
  { type: "Snack", order: 3, foods: [] },
  { type: "Dinner", order: 4, foods: [] },
];

/**
 * Builds the POST body for `/actions/meal-planner/create-meal-plan`, shared
 * between CreateMealPlanModal.js's own cards and ExistingPlans.js's inline
 * "+ Add" dropdown — same request either way, just two different triggers.
 *
 * @param {Object} args
 * @param {"simplified"|"detailed"} args.planMode
 * @param {number} args.utcStartDate - YYYYMMDD, from the owning nutrition plan
 * @param {number} args.utcEndDate - YYYYMMDD, from the owning nutrition plan
 * @param {string} args.athleteId
 * @param {string} args.coachId
 * @param {string} args.nutritionPlanId
 * @returns {Object} body, including requestId
 */
export const buildCreateMealPlanBody = ({
  planMode,
  utcStartDate,
  utcEndDate,
  athleteId,
  coachId,
  nutritionPlanId,
}) => {
  let body = {
    planMode,
    utcStartDate,
    utcEndDate,
    published: false,
    athleteId,
    coachId,
    type: "plan",
    nutritionPlanId,
  };

  if (planMode === "detailed") {
    body = { ...body, weeks: calculateWeeksWithDays(utcStartDate, utcEndDate) };
  }

  if (planMode === "simplified") {
    body = {
      ...body,
      trainingDayOptions: [{ title: "Option 1", meals: DEFAULT_MEALS }],
      restDayOptions: [{ title: "Option 1", meals: DEFAULT_MEALS }],
      selectedOptions: [],
    };
  }

  return { ...body, requestId: simpleHash(JSON.stringify(body)) };
};

/**
 * Display title for a meal plan — real `title` when it has one (only ever
 * true for a plan built from a Library template), otherwise the same
 * "Simplified/Detailed Meal Plan" copy the create dropdown itself offers.
 * Shared between ExistingPlans.js's per-row title and the PDF-vs-meal-plan
 * conflict banner so both ever call one plan by the same name.
 *
 * @param {Object} mealPlan
 * @param {Function} t - i18next translate function
 * @returns {string|null} null when there's no meal plan at all
 */
export const resolveMealPlanTitle = (mealPlan, t) => {
  if (!mealPlan) return null;
  if (mealPlan.title) return mealPlan.title;
  return t(
    `createMealPlanModal.${mealPlan.planMode === "detailed" ? "detailedTitle" : "simplifiedTitle"}`
  );
};

/**
 * Confirms with the coach, then deletes a meal plan — same request
 * MealPlannerHeader.js's own "More" menu delete option sends, shared so
 * ExistingPlans.js's row-level delete button is the same action, not a
 * second copy of it.
 *
 * @param {Object} args
 * @param {string} args.mealPlanId
 * @param {string} args.nutritionPlanId
 * @param {string} args.athlete - proxyAthlete id
 * @param {Function} args.t - i18next translate function
 */
export const deleteMealPlan = ({ mealPlanId, nutritionPlanId, athlete, t }) => {
  if (!(process.browser && confirm(t("mealPlannerHeader.deleteConfirm")))) return;

  submit(
    `/actions/meal-planner/delete-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}&nutritionPlanId=${nutritionPlanId}`,
    {
      method: "POST",
    }
  );
};

/**
 * Check if a meal plan template is currently being processed (generation status is pending)
 *
 * @param {Object} template - The meal plan template object
 * @returns {boolean} - True if the template is being processed, false otherwise
 */
export const isTemplateProcessing = (template) => {
  const generation = template?.generation;
  return (
    generation &&
    generation.status === "pending" &&
    (generation.taskId || generation._id || generation.id)
  );
};

/**
 * Calculate the meals structure and title(s) for new option(s)
 * based on the last option of the same day type (training or rest)
 *
 * @param {string} tabState - "training" or "rest"
 * @param {Object} mealPlan - The meal plan object with trainingDayOptions and restDayOptions
 * @param {number} [count=1] - Number of options to generate (default: 1)
 * @returns {Object|Array} - If count === 1: Object with `title` string and `meals` array.
 *                          If count > 1: Array of objects, each with `title` string and `meals` array.
 */
export const calculateNewOptionMealPlannerData = (tabState, mealPlan, count = 1) => {
  // Get the appropriate options array based on tabState
  const optionsArray =
    tabState === "training"
      ? mealPlan?.trainingDayOptions || []
      : mealPlan?.restDayOptions || [];

  // Get the last option of the same type
  const lastOption = optionsArray[optionsArray.length - 1];

  // Default meals structure when no previous option exists
  const defaultMeals = [
    { type: "Breakfast", order: 1, foods: [] },
    { type: "Lunch", order: 2, foods: [] },
    { type: "Snack", order: 3, foods: [] },
    { type: "Dinner", order: 4, foods: [] },
  ];

  // Copy meals structure from last option, but with empty foods arrays
  // If no last option exists or meals array is empty, use default meals structure
  const meals =
    lastOption?.meals && lastOption.meals.length > 0
      ? lastOption.meals.map((meal) => ({
          type: meal.type,
          order: meal.order,
          foods: [],
        }))
      : defaultMeals;

  // Extract all existing option numbers from titles
  const existingNumbers = optionsArray
    .map((option) => {
      const title = option?.title || "";
      const match = title.match(/^Option (\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((num) => num !== null)
    .sort((a, b) => a - b);

  // Find available numbers (gaps first, then sequential after max)
  const getAvailableNumbers = (count) => {
    const available = [];
    const existingSet = new Set(existingNumbers);

    if (existingNumbers.length === 0) {
      // No existing options, return sequential numbers starting from 1
      return Array.from({ length: count }, (_, i) => i + 1);
    }

    const maxExisting = existingNumbers[existingNumbers.length - 1];

    // First, check for gaps in the sequence (1 to maxExisting)
    for (let i = 1; i <= maxExisting && available.length < count; i++) {
      if (!existingSet.has(i)) {
        available.push(i);
      }
    }

    // If we still need more numbers, continue from maxExisting + 1
    let nextNumber = maxExisting + 1;
    while (available.length < count) {
      available.push(nextNumber);
      nextNumber++;
    }

    return available.slice(0, count);
  };

  const availableNumbers = getAvailableNumbers(count);

  // If generating a single option, return object format (backward compatible)
  if (count === 1) {
    const title = `Option ${availableNumbers[0]}`;
    return {
      title,
      meals,
    };
  }

  // If generating multiple options, return array format
  return availableNumbers.map((optionNumber) => ({
    title: `Option ${optionNumber}`,
    meals,
  }));
};